import { AppIcon, type IconName } from '@/components/ui/icons';
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { brandColorAlpha } from '@/lib/chartTokens';
import { useAuth } from '../contexts/AuthContext';
import type { MvpAccountRole } from '../types';
import UserBadge from './UserBadge';
import { resolveIdentityRoles } from '../lib/auth/identityRoles';
import {
  DEMO_ACCOUNT_ROLE_GROUPS,
  DEMO_ACCOUNT_ROLE_LABELS,
  DEMO_SCOPED_OVERLAYS,
} from '../lib/auth/demoRoleNavigation';
import { Avatar } from '@/features/ui/components/Avatar';

interface RoleSwitcherProps {
  onClose: () => void;
}


const RoleSwitcher: React.FC<RoleSwitcherProps> = ({ onClose }) => {
  const { user, switchRole, resetDemoSession, logout, isDemoMode } = useAuth();
  const navigate = useNavigate();
  // Single canonical role source: display the resolved account/platform role.
  const identity = resolveIdentityRoles(user);
  const activeAccountRole = identity.platformRole ?? identity.accountRole;

  const handleRoleSwitch = (role: MvpAccountRole) => {
    if (!isDemoMode) {
      console.warn('Role switching is only available in demo mode');
      return;
    }
    const transition = switchRole(role);
    if (!transition) return;
    onClose();
    navigate(transition.landing, { replace: true });
  };

  // Scoped-relationship OVERLAY (demo): mounts a Workspace Member identity
  // whose access derives ONLY from the named scoped relationship. The investor
  // AuthoritySessionBoundary discards the old identity's providers and routes.
  // Replace history in the same event, before the fresh router mounts.
  const handleOverlaySwitch = (overlayKey: string) => {
    if (!isDemoMode) {
      console.warn('Role switching is only available in demo mode');
      return;
    }
    const transition = switchRole(overlayKey as MvpAccountRole);
    if (!transition) return;
    onClose();
    navigate(transition.landing, { replace: true });
  };

  const handleDemoReset = () => {
    const transition = resetDemoSession();
    if (!transition) return;
    onClose();
    navigate(transition.landing, { replace: true });
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50"
        onClick={onClose}
      />

      {/* Menu */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        className="absolute right-0 top-full mt-2 w-80 bg-surface-card rounded-lg [box-shadow:var(--depth-2-shadow)] border border-border-subtle/50 z-50 overflow-hidden"
      >
        {/* Current User */}
        <div className="card-padding-sm border-b border-border-subtle/50 bg-accent-primary/10">
          <div className="flex items-center space-x-md">
            <div className="relative">
              <Avatar
                name={user?.name || 'AX1 user'}
                src={user?.avatar}
                size={48}
                colorSeed={user?.id || user?.email || user?.name}
                className="shadow-sm"
                decorative
              />
              <div className="absolute -bottom-1 -right-1">
                <UserBadge user={user || undefined} size="sm" variant="minimal" showLabel={false} />
              </div>
            </div>
            <div className="flex-1">
              <p className="font-medium text-text-primary">{user?.name}</p>
              <p className="text-sm text-text-muted">{user?.email}</p>
              <div className="mt-1">
                <UserBadge user={user || undefined} size="sm" />
              </div>
            </div>
          </div>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {DEMO_ACCOUNT_ROLE_GROUPS.map((group, groupIndex) => (
            <div key={group.id} className={`p-3 ${groupIndex ? 'border-t border-border-subtle' : ''}`}>
              <div className="mb-md flex items-start space-x-sm">
                <div className={`h-3 w-3 rounded-full ${group.dotClass}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="ax-section-label text-text-muted">
                      {group.label}
                    </p>
                    <span className="text-[10px] tabular-nums text-text-muted">
                      {group.roles.length}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-text-muted">{group.description}</p>
                </div>
              </div>
              {group.roles.map(role => {
                const isActive = activeAccountRole === role;

                return (
                  <motion.button
                    key={role}
                    whileHover={{ backgroundColor: brandColorAlpha('--color-primary-500-rgb', 0.05, '59 130 246') }}
                    onClick={() => handleRoleSwitch(role)}
                    aria-pressed={isActive}
                    className={`mb-sm flex w-full items-center space-x-md rounded-lg border px-3 py-3 text-left transition-colors ${
                      isActive
                        ? 'border-accent-primary/30 bg-accent-primary/10'
                        : 'border-transparent hover:bg-surface-muted'
                    } ${!isDemoMode ? 'cursor-not-allowed opacity-50' : ''}`}
                    disabled={!isDemoMode}
                  >
                    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                      isActive ? 'bg-accent-primary/10' : 'bg-surface-chip'
                    }`}>
                      <AppIcon
                        name={(group.id === 'platform' ? 'shield' : 'building2') as IconName}
                        className={`h-4 w-4 ${isActive ? 'text-accent-primary' : 'text-text-muted'}`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-sm">
                        <span className="font-medium text-text-primary">{DEMO_ACCOUNT_ROLE_LABELS[role]}</span>
                        {isActive && <div className="h-2 w-2 rounded-full bg-primary-600" />}
                      </div>
                      <p className="text-xs text-text-muted">Account role</p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          ))}

          {/* Scoped-relationship OVERLAYS — Workspace Member + relationship. */}
          <div className="p-3 border-t border-border-subtle">
            <div className="mb-md flex items-start space-x-sm">
              <div className="h-3 w-3 rounded-full bg-status-info" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="ax-section-label text-text-muted">
                    Scoped Relationship Overlays
                  </p>
                  <span className="text-[10px] tabular-nums text-text-muted">{DEMO_SCOPED_OVERLAYS.length}</span>
                </div>
                <p className="mt-0.5 text-[11px] text-text-muted">
                  Workspace Member + scoped relationship (not account roles)
                </p>
              </div>
            </div>
            {DEMO_SCOPED_OVERLAYS.map(overlay => (
              <motion.button
                key={overlay.key}
                whileHover={{ backgroundColor: brandColorAlpha('--color-primary-500-rgb', 0.05, '59 130 246') }}
                onClick={() => handleOverlaySwitch(overlay.key)}
                className={`mb-sm flex w-full items-center space-x-md rounded-lg border border-transparent px-3 py-3 text-left transition-all hover:bg-surface-muted ${!isDemoMode ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={!isDemoMode}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-chip">
                  <AppIcon name="users" className="h-4 w-4 text-status-info-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-text-primary">{overlay.label}</span>
                  <p className="text-xs text-text-muted">{overlay.description} · {overlay.resourceLabel}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Demo Notice & Logout */}
        <div className="border-t border-border-subtle/50 bg-surface-muted/50">
          <div className="p-3">
            <div className="bg-accent-primary/10 border border-accent-primary/30 rounded-xl p-2 mb-md">
              <p className="text-xs text-accent-primary font-medium">Demo Mode {isDemoMode ? 'Active' : 'Inactive'}</p>
              <p className="text-xs text-accent-primary">
                {isDemoMode
                  ? 'Choose any canonical persona to inspect its navigation and pages'
                  : 'Role switching is only available in demo mode'}
              </p>
            </div>
            <motion.button
              whileHover={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}
              onClick={logout}
              className="w-full flex items-center space-x-md px-3 py-2 text-status-danger-foreground hover:text-status-danger-foreground rounded-lg transition-all"
            >
              <AppIcon name="logOut" className="w-4 h-4" />
              <span className="font-medium">Sign Out</span>
            </motion.button>
            {isDemoMode && (
              <motion.button
                whileHover={{ backgroundColor: brandColorAlpha('--color-primary-500-rgb', 0.05, '59 130 246') }}
                onClick={handleDemoReset}
                className="mt-sm w-full flex items-center space-x-md px-3 py-2 text-text-secondary hover:text-text-primary rounded-lg transition-all"
              >
                <AppIcon name="rotateCcw" className="w-4 h-4" />
                <span className="font-medium">Reset Demo Session</span>
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default RoleSwitcher;
